import { useId, useState } from "react";
import { useCancelEmailChange, useRequestEmailChange } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { useStepUp } from "../../auth/StepUpProvider";
import { MutationError, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { useFormat } from "../../lib/useFormat";

/**
 * Section 13.2, #182: the email on the account, which is also the sign-in.
 * A change waits for the new address to follow its link; until then the old
 * one keeps working and has been told, and the change can be canceled here -
 * which is where the notice to the old address points (`/account#email`).
 */
export function EmailSection() {
  const { me } = useAuth();
  const { ensureFresh } = useStepUp();
  const request = useRequestEmailChange();
  const cancel = useCancelEmailChange();
  const fmt = useFormat();
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState("");
  const id = useId();
  const pending = me?.pendingEmail;
  const current = me?.email ?? me?.subject;

  if (me?.identityProvider) {
    return (
      <div id="email" className="mt-4 scroll-mt-6 border-t border-neutral-200 pt-4 dark:border-neutral-800">
        <p className="text-sm"><span className="font-medium">Email:</span> <span className="break-words">{current}</span></p>
        <p className="mt-1 text-xs text-neutral-500">Your email comes from {me.identityProvider}. Change it there, then sign in again.</p>
      </div>
    );
  }

  return (
    <div id="email" className="mt-4 scroll-mt-6 border-t border-neutral-200 pt-4 dark:border-neutral-800">
      <p className="text-sm"><span className="font-medium">Email:</span> <span className="break-words">{current}</span></p>
      <p className="mt-1 text-xs text-neutral-500">You sign in with this address, and Starsach writes to it.</p>

      {pending ? (
        <div role="status" className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          <p>
            Waiting for <span className="break-words font-medium">{pending.address}</span> to be confirmed. We sent a link there; it works until <time dateTime={pending.expiresAt}>{fmt.when(pending.expiresAt)}</time>.
          </p>
          <p className="mt-1">Until then you keep signing in with {current}. We wrote to it as well, with a way to cancel.</p>
          <button type="button" className={`${btnSecondary} mt-3`} disabled={cancel.isPending || Boolean(me?.viewAs)} onClick={() => cancel.mutate()}>
            {cancel.isPending ? "Canceling…" : "Cancel this change"}
          </button>
        </div>
      ) : editing ? (
        <form
          className="mt-3 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            // Section 13.3: a change of sign-in address needs a sign-in from the last fifteen minutes.
            void ensureFresh().then(
              () => request.mutate(address.trim(), { onSuccess: () => { setEditing(false); setAddress(""); } }),
              () => undefined,
            );
          }}
        >
          <label htmlFor={`${id}-email`} className="text-sm">
            <span className="block font-medium">New email address</span>
            <input id={`${id}-email`} type="email" required maxLength={254} autoComplete="email" className={`${inputClass} w-72 max-w-full`} value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <button type="submit" className={btnPrimary} disabled={!address.trim() || request.isPending}>
            {request.isPending ? "Sending…" : "Send confirmation link"}
          </button>
          <button type="button" className={btnSecondary} onClick={() => { setEditing(false); setAddress(""); request.reset(); }}>Cancel</button>
          <span className="basis-full text-xs text-neutral-500">We send a link to the new address. Your email changes only when it is opened, within 24 hours, and you then sign in again with the new address.</span>
        </form>
      ) : (
        <button type="button" className={`${btnSecondary} mt-3`} disabled={Boolean(me?.viewAs)} onClick={() => setEditing(true)}>Change email</button>
      )}
      <MutationError of={[request, cancel]} className="mt-3" />
    </div>
  );
}
